import { useMemo } from 'react';

import { UseMutationResult } from '@tanstack/react-query';

import { Badge, BadgeVariant } from '@components/badge';
import { Button } from '@components/button';
import { SortableList } from '@components/drag-and-drop';
import { DraggableComponentProps } from '@components/drag-and-drop/sortable-list';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyStateCell } from '@components/empty-state';
import {
  setContinueRegistry,
  useDeleteBlockedModal,
  useDeleteModal,
} from '@components/generic-modals';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCopy } from '@components/icons/copy';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { IconTraveller } from '@components/icons/traveller';
import { DismissibleInfoBox } from '@components/info-box';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { MiddleColumnPage } from '@components/page-containers/page-containers';
import { useIsSidebarActive } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { reorderList } from '@utils/collections';

import {
  useCompanyTravellerAttributeTemplates,
  useCreateTravellerAttributeTemplate,
  useDeleteTravellerAttributeTemplate,
  useDuplicateTravellerAttributeTemplate,
  useUpdateTravellerAttributeTemplate,
  useUpdateTravellerAttributeTemplateOrders,
} from 'api/attributes/hooks';
import {
  TravellerAttributeTemplate,
  TravellerAttributeTemplateData,
  loadCompanyTravellerAttributeTemplate,
} from 'api/attributes/requests';
import { FieldCard } from 'ui/authenticated/settings/pages/attributes/field-card';
import { useFieldFormSidebar } from 'ui/authenticated/settings/pages/attributes/sidebars/field-form';

export const TravellerAttributes = ({ searchStr }: { searchStr: string }) => {
  return (
    <PageContent>
      <TravellerAttributeList searchStr={searchStr} />
    </PageContent>
  );
};

const useDeleteAttributeModal = () => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();
  const showCannotDeleteModal = useDeleteBlockedModal();
  const removeMutation = useDeleteTravellerAttributeTemplate();

  return async (attribute: TravellerAttributeTemplate) => {
    const attributeTemplateDetails = await loadCompanyTravellerAttributeTemplate(attribute.shortId);
    const isDeleteBlocked = attributeTemplateDetails?.isFieldMapped;
    if (!isDeleteBlocked) {
      showDeleteModal({
        title: t('deleteAttribute'),
        description: t('deleteAttributeDescription'),
        onConfirm: () => {
          removeMutation.mutateAsync(attribute.shortId);
        },
      });
    } else {
      showCannotDeleteModal({ description: t('cannotDelete') });
    }
  };
};

const TravellerAttributeList = ({ searchStr }: { searchStr: string }) => {
  const { t } = useScopedTranslate(locale);
  const showAddAttributeSidebar = useFieldFormSidebar();
  const createMutation = useCreateTravellerAttributeTemplate();
  const updateMutation = useUpdateTravellerAttributeTemplate();
  const updateOrdersMutation = useUpdateTravellerAttributeTemplateOrders();
  const duplicateMutation = useDuplicateTravellerAttributeTemplate();
  const showDeleteModal = useDeleteAttributeModal();

  const { data: allAttributes } = useCompanyTravellerAttributeTemplates({ systemAttributes: true });

  const systemAttributes = useMemo(
    () =>
      (allAttributes ?? []).filter(
        (attribute) =>
          attribute.isSystemAttribute && matchesSearchStr(attribute.templateTitle, searchStr),
      ),
    [allAttributes, searchStr],
  );

  const userAttributes = useMemo(
    () => (allAttributes ?? []).filter((attribute) => !attribute.isSystemAttribute),
    [allAttributes],
  );

  const filteredUserAttributes = useMemo(
    () =>
      userAttributes.filter((attribute) => matchesSearchStr(attribute.templateTitle, searchStr)),
    [userAttributes, searchStr],
  );

  const changeAttributeOrder = (activeIndex: number, overIndex: number) => {
    // SortableList passes indices within the filtered list — translate via
    // shortId so the reorder applies to the full userAttributes array, not
    // just the search-filtered subset.
    const activeShortId = filteredUserAttributes[activeIndex]?.shortId;
    const overShortId = filteredUserAttributes[overIndex]?.shortId;
    if (!activeShortId || !overShortId) return;
    const fullActive = userAttributes.findIndex((a) => a.shortId === activeShortId);
    const fullOver = userAttributes.findIndex((a) => a.shortId === overShortId);
    if (fullActive < 0 || fullOver < 0) return;

    const newUserAttributes = reorderList(userAttributes, fullActive, fullOver);
    updateOrdersMutation.mutate(
      newUserAttributes.reduce((acc, curr, idx) => ({ ...acc, [curr.shortId]: idx }), {}),
    );
  };

  const addAttribute = () => {
    showAddAttributeSidebar({
      isTravellerAttribute: true,
      onCreate: createMutation.mutateAsync,
    });
  };

  const hasNoMatches =
    Boolean(searchStr) && filteredUserAttributes.length === 0 && systemAttributes.length === 0;

  return (
    <MiddleColumnPage>
      <Stack space={4}>
        <Stack space={2}>
          <Text font="bold18" text={t('travellerFields')} />
          <Text font="regular14" color="textSecondary" text={t('travellerFieldsDescription')} />
        </Stack>
        {hasNoMatches ? (
          <EmptySearchState variant="small" skipMargin />
        ) : (
          <>
            <Stack space={2}>
              {systemAttributes.map((attribute) => (
                <SystemAttribute key={attribute.shortId} attribute={attribute} />
              ))}
              {userAttributes.length === 0 && !searchStr && (
                <EmptyStateCell
                  icon={<IconTraveller />}
                  text={t('noAttributes')}
                  action={
                    <Button
                      iconLeft={<IconAddPlus />}
                      variant="secondary"
                      onClick={addAttribute}
                      text={t('addAttribute')}
                    />
                  }
                />
              )}
              <SortableList<AttributeComponentProps, TravellerAttributeTemplate>
                onChange={changeAttributeOrder}
                items={filteredUserAttributes}
                idProperty="shortId"
                DraggableComponent={Attribute}
                componentProps={{
                  update: updateMutation,
                  duplicate: duplicateMutation,
                  showDeleteModal,
                }}
              />
            </Stack>
            <DismissibleInfoBox
              storageKey="traveller-fields-recommendation-dismissed"
              variant="indigo"
              icon={<IconFormIndicatorInfo color="indigo100" />}
              text={t('fieldRecommendation')}
            />
          </>
        )}
      </Stack>
    </MiddleColumnPage>
  );
};

type AttributeComponentProps = {
  update: UseMutationResult<
    TravellerAttributeTemplate,
    unknown,
    { data: TravellerAttributeTemplateData; shortId: string },
    unknown
  >;
  duplicate: UseMutationResult<TravellerAttributeTemplate, unknown, string, unknown>;
  showDeleteModal: (attribute: TravellerAttributeTemplate) => Promise<void>;
};

const Attribute = ({
  item: attribute,
  attributes,
  listeners,
  update,
  duplicate,
  showDeleteModal,
}: AttributeComponentProps & DraggableComponentProps<TravellerAttributeTemplate>) => {
  const { t } = useScopedTranslate(locale);
  const showAttributeSidebar = useFieldFormSidebar();
  const isActive = useIsSidebarActive(attribute.shortId);

  const openEditSidebar = () => {
    const showFn = () => {
      showAttributeSidebar(
        {
          isTravellerAttribute: true,
          onEdit: (data: TravellerAttributeTemplateData) =>
            update.mutateAsync({ data, shortId: attribute.shortId }),
          attributeId: attribute.shortId,
        },
        { id: attribute.shortId },
      );
    };
    setContinueRegistry({ id: attribute.shortId, onContinue: showFn });
    showFn();
  };

  return (
    <FieldCard
      attributeType={attribute.attributeType}
      title={attribute.templateTitle}
      dragAttributes={attributes}
      dragListeners={listeners}
      isActive={isActive}
      hasTypeColor={false}
      textSize={14}
      onClick={openEditSidebar}
      rightContent={
        <Stack orientation="horizontal" space={2} align="center">
          <Badge
            variant={INPUT_BADGE_VARIANT[attribute.input]}
            text={t(attribute.input)}
            size="md"
          />
          <Menu
            buttonProps={{
              variant: 'ghost',
              iconRight: <IconMoreVertical />,
              stopPropagation: true,
            }}
          >
            <Menu.Item onSelect={openEditSidebar} label={t('core.edit')} icon={<IconEdit />} />
            <Menu.Item
              onSelect={() => duplicate.mutate(attribute.shortId)}
              label={t('core.duplicate')}
              icon={<IconCopy />}
            />
            <Menu.Item
              onSelect={() => showDeleteModal(attribute)}
              variant="danger"
              label={t('core.delete')}
              icon={<IconTrash />}
            />
          </Menu>
        </Stack>
      }
    />
  );
};

const SystemAttribute = ({ attribute }: { attribute: TravellerAttributeTemplate }) => {
  return (
    <FieldCard
      attributeType={attribute.attributeType}
      title={attribute.templateTitle}
      hasTypeColor={false}
      textSize={14}
    />
  );
};

const INPUT_BADGE_VARIANT: Record<TravellerAttributeTemplate['input'], BadgeVariant> = {
  disabled: 'neutral',
  optional: 'orange',
  required: 'green',
};

const matchesSearchStr = (title: string, searchStr: string) => {
  if (!searchStr) return true;
  return title.toLowerCase().includes(searchStr.toLowerCase());
};

const locale = {
  en: {
    travellerFields: 'Traveller fields',
    travellerFieldsDescription: 'Add and configure the fields that travellers should have',
    noAttributes: 'No fields added',
    disabled: 'Disabled',
    optional: 'Optional',
    required: 'Required',
    addAttribute: 'Create traveller field',
    deleteAttribute: 'Delete field',
    deleteAttributeDescription: 'Are you sure you want to delete this field?',
    cannotDelete: 'You cannot delete this field, since it has been used in a journey.',
    fieldRecommendation:
      'For clarity and usability, we recommend only adding fields you intend to use for every traveller — for example: Department, Supervisor, Birthday.',
  },
};
